import React from 'react';
import RiseSurveyInterstitialControls from 'screens/InsomniaSurvey/components/RiseSurveyControls/RiseSurveyInterstitialControls';
import partnershipLockup from './assets/rise-x-sleepiorx-partnership.png';
import styles from './SleepioRxInterstitials.module.css';

export type SleepioInfoScreen =
  | 'intro'
  | 'clinicianReview'
  | 'scienceRegularTrouble'
  | 'scienceOtherApproaches'
  | 'scienceCbti'
  | 'framingFit'
  | 'framingNothingWorked'
  | 'framingRootCause'
  | 'framingWhatItIs'
  | 'framingInsurance'
  | 'framingProgress';

export type ScreenContent = {
  kicker: string;
  title: string;
  body: React.ReactNode;
  cta: string;
  bullets?: string[];
  finePrint?: string;
  // When true, render the Rise × SleepioRx co-brand lockup badge instead of the
  // text kicker (used on the partnership/fit screen).
  lockup?: boolean;
};

// Copy mirrors the clinically/product-reviewed strings in nyx
// (src/features/SleepioRx/upsell/configs/sleepioRxSurveyConfig.tsx). Do not
// reword without coordinating with that file — these are reviewed strings.
// The web card renders the reviewed copy + bullet points; nyx's richer visual
// treatments (animated root-cause build, stat gauges, X-mark lists, partnership
// lockup) are intentionally simplified to the standard funnel card here.
// Exported so the combined fit-question screen (SleepioFitWithScienceInterstitial)
// can reuse the science-screen copy instead of duplicating it.
export const getSleepioInfoContent = (screen: SleepioInfoScreen): ScreenContent => {
  const content: Record<SleepioInfoScreen, ScreenContent> = {
    intro: {
      kicker: 'New insight',
      title: 'Your responses helped us assess your sleep quality.',
      body: 'Sleep scientists stand behind our sleep quality score, the Insomnia Severity Index (ISI).',
      cta: 'Continue',
    },
    clinicianReview: {
      kicker: 'Expert review',
      title: 'A licensed clinician reviews your answers — no video visit needed.',
      body: "You won't need to book or sit through a video appointment. A licensed sleep psychologist reviews your responses and, if it's the right fit, sends your personalized next steps. You get real clinical oversight — delivered through a digital program you can start on your own schedule.",
      cta: 'Continue',
    },
    scienceRegularTrouble: {
      kicker: 'Guidance fit',
      title: 'What the science says:',
      body: "Insomnia isn't just about one bad night — it's a pattern that affects your days as much as your nights.",
      bullets: [
        "Poor sleep quality can show up as daytime fatigue, mood changes, or feeling like you're never fully rested.",
        "When this happens 3+ nights per week for 3+ months, it's chronic insomnia — and there's a clinical treatment for it.",
      ],
      cta: 'Continue',
    },
    scienceOtherApproaches: {
      kicker: 'Guidance fit',
      title: 'What the science says:',
      body: "Sleep hygiene and pills don't address the root cause of insomnia.",
      bullets: [
        "Caffeine limits, blue-light glasses, melatonin — these help with general sleep issues but don't fix chronic insomnia.",
        'Sleep pills can build tolerance and dependency. They mask the problem without resolving it.',
      ],
      cta: 'Continue',
    },
    scienceCbti: {
      kicker: 'CBT-I fit',
      title: 'What the science says:',
      body: 'CBT-I is the first-line treatment for chronic insomnia.',
      bullets: [
        'CBT-I helps 70-80% of people with chronic insomnia fall asleep faster, stay asleep longer, and wake up refreshed.',
        "It's the first-line recommendation of the American Academy of Sleep Medicine.",
      ],
      cta: 'Continue',
    },
    framingFit: {
      kicker: 'Rise + SleepioRx',
      lockup: true,
      title: 'You may be a fit for SleepioRx.',
      body: 'Through our partnership with Big Health, you may qualify for SleepioRx — the gold-standard digital treatment for chronic insomnia, prescribed by a Rise clinician and covered by your insurance.',
      cta: 'Tell Me More',
    },
    framingNothingWorked: {
      kicker: 'SleepioRx',
      title: "Here's why nothing you've tried has worked.",
      body: "You've probably tried:",
      bullets: ['Going to bed earlier', 'Melatonin', 'Sleeping pills', 'Blue light glasses', 'Reading before bed'],
      cta: 'Show Me Why',
    },
    framingRootCause: {
      kicker: 'Root cause',
      title: 'None of it works because none of it actually treats insomnia.',
      body: "Insomnia isn't a bad habit — it's a learned pattern your brain developed. Sleep hygiene alone can't fix it; in fact, it often makes it worse.",
      bullets: [
        'CBT-I is the only clinical treatment that rewires this pattern.',
        "It's the first-line recommendation of the American Academy of Sleep Medicine.",
      ],
      cta: 'Show Me How It Works',
    },
    framingWhatItIs: {
      kicker: 'What it is',
      title: 'SleepioRx targets the habits and thoughts that fuel insomnia.',
      body: "It's a skills program, not talk therapy. Over 6 weeks in the SleepioRx app, you'll learn and apply proven CBT-I techniques — building on each as you go.",
      bullets: ['76% achieve healthy sleep', '63% less time awake at night', '55% less time to fall asleep'],
      finePrint: 'Espie et al. 2012; Tarnai et al. 2025',
      cta: 'Continue',
    },
    framingInsurance: {
      kicker: 'Insurance',
      title: 'Available through your existing insurance.',
      body: "Rise partnered with Big Health to bring SleepioRx to you through your insurance. Most patients pay $0* through the SleepioRx Copay Program, and we'll enroll you automatically.",
      bullets: [
        'Skip the waitlist',
        'No live appointment required',
        'Reviewed within 24 hours by a licensed clinician',
      ],
      finePrint: '* Standard outpatient cost share may apply. An insurance Explanation of Benefits is not a bill.',
      cta: 'Continue',
    },
    framingProgress: {
      kicker: 'Progress',
      title: 'See real progress in your insomnia symptoms.',
      body: 'SleepioRx is built to address the root cause, not just symptoms.',
      bullets: [
        'Proven results — 4 out of 5 see improvement',
        'Addresses the root cause, not just symptoms',
        'Builds core skills for life',
      ],
      cta: 'Get Started',
    },
  };

  return content[screen];
};

// Shared presentational card for a SleepioRx education screen's content.
export const SleepioInfoCard: React.FC<{content: ScreenContent}> = ({content}) => (
  <div className={styles.heroCard}>
    {content.lockup ? (
      <div className={styles.lockupBanner}>
        <img src={partnershipLockup} alt="Rise × SleepioRx" className={styles.lockupImage} />
      </div>
    ) : (
      <div className={styles.kicker}>{content.kicker}</div>
    )}
    <h1 className={styles.title}>{content.title}</h1>
    {content.body ? <p className={styles.body}>{content.body}</p> : null}
    {content.bullets ? (
      <ul className={styles.bulletList}>
        {content.bullets.map(item => (
          <li key={item} className={styles.bullet}>
            {item}
          </li>
        ))}
      </ul>
    ) : null}
    {content.finePrint ? <p className={styles.finePrint}>{content.finePrint}</p> : null}
  </div>
);

export const SleepioInfoInterstitial: React.FC<{screen: SleepioInfoScreen}> = ({screen}) => {
  const content = getSleepioInfoContent(screen);

  return (
    <div className={`${styles.container} ${styles.centered}`}>
      <SleepioInfoCard content={content} />
      <RiseSurveyInterstitialControls continueButtonTitle={content.cta} variant="purple" />
    </div>
  );
};

export default SleepioInfoInterstitial;
